import {
  unique,
  collectGroupBuckets,
  findColorFamilyBucket,
  getSwatchLabel,
  getSwatchColor,
  getSwatchImage,
  getColorPatternLabel,
  fetchProductGroups,
  fetchProducts,
} from "../storefront/color-family";

const cardSelector = "[data-product-card][data-product-group-id]";
const swatchContainerSelector = "[data-product-card-swatches]";
const maxVisibleSwatches = 4;

function getStyleGroupTag(tags) {
  const styleGroupTag = Array.isArray(tags) ? tags.find((tag) => tag.includes("style_group:")) : null;

  return styleGroupTag ? styleGroupTag.replace("style_group:", "").trim() : "";
}

function getProductUrl(product) {
  return product?.handle ? `/products/${product.handle}` : "#";
}

function isSwatchProductFromTags(tags) {
  return (
    Array.isArray(tags) &&
    (tags.includes("product_type:Swatch") || tags.includes("Mega Swatches") || tags.includes("Multi Swatch"))
  );
}

function resolveDisplayPrice(price, isSwatchProduct) {
  if (isSwatchProduct && !window.customerHasFreeSwatch) {
    return { price: 0, compareAtPrice: price };
  }

  return { price, compareAtPrice: 0 };
}

function numericIdFromGid(gid) {
  const match = typeof gid === "string" && gid.match(/(\d+)$/);

  return match ? match[1] : null;
}

function applyAddToBagTarget(card, product) {
  const button = card.querySelector("[data-product-card-add-to-bag]");

  if (!button) {
    return;
  }

  delete button.dataset.state;
  button.disabled = false;

  delete button.dataset.deliveryOption;

  const variantId = numericIdFromGid(product.variants?.nodes?.[0]?.id);

  if (isSwatchProductFromTags(product.tags) && variantId) {
    const colorLabel = getColorPatternLabel(product);

    button.dataset.variantId = variantId;
    button.dataset.color = colorLabel;
    button.dataset.colorCode = colorLabel.split(" - ").pop().trim();
    button.dataset.styleGroup = getStyleGroupTag(product.tags);
    button.hidden = false;
  } else {
    button.hidden = true;
    delete button.dataset.variantId;
    delete button.dataset.color;
    delete button.dataset.colorCode;
    delete button.dataset.styleGroup;
  }
}

function sortProductsForCard(products, currentProductId) {
  return [...products].sort((a, b) => {
    if (a.id === currentProductId) {
      return -1;
    }

    if (b.id === currentProductId) {
      return 1;
    }

    return 0;
  });
}

function createSwatch(product, currentProductId, onSelect) {
  const swatch = document.createElement("button");

  const label = getSwatchLabel(product);
  const color = getSwatchColor(product);
  const image = getSwatchImage(product);

  swatch.type = "button";
  swatch.className = "product-card__swatch";

  swatch.dataset.productCardSwatch = "";
  swatch.dataset.productId = product.id;

  swatch.setAttribute("aria-label", label ? `Select ${label}` : "Select color");
  swatch.title = label;

  if (product.id === currentProductId) {
    swatch.classList.add("is-active");
    swatch.setAttribute("aria-pressed", "true");
  } else {
    swatch.setAttribute("aria-pressed", "false");
  }

  if (color) {
    swatch.style.backgroundColor = color;
  }

  if (image) {
    swatch.style.backgroundImage = `url("${image}")`;
  }

  swatch.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    onSelect(product, swatch);
  });

  return swatch;
}

function createMoreIndicator(count) {
  const more = document.createElement("span");

  more.className = "product-card__swatch-more body-xsmall";
  more.textContent = `+${count}`;

  return more;
}

function updateProductCard(card, product) {
  if (!card || !product) {
    return;
  }

  card.dataset.productId = product.id;

  const productUrl = getProductUrl(product);

  card.querySelectorAll("[data-product-card-link]").forEach((link) => {
    link.href = productUrl;
  });

  const title = product.title?.split("|")?.[0]?.trim() || product.title || "";
  const titleElement = card.querySelector("[data-product-card-title]");

  if (titleElement) {
    titleElement.textContent = title;
  }

  const imageContainer = card.querySelector("[data-product-card-image]");
  const primaryImage = product.images?.nodes?.[0] || product.featuredImage || null;

  if (imageContainer && primaryImage?.url) {
    const image = imageContainer.querySelector("img");

    if (image) {
      image.src = primaryImage.url;
      image.srcset = primaryImage.url;
      image.alt = primaryImage.altText || product.title || "";
    }

    imageContainer.querySelectorAll("source").forEach((source) => {
      source.srcset = primaryImage.url;
    });
  }

  const hoverContainer = card.querySelector("[data-product-card-image-hover]");
  const hoverImage = product.images?.nodes?.[1] || null;

  if (hoverContainer) {
    const image = hoverContainer.querySelector("img");

    if (image && hoverImage?.url) {
      image.src = hoverImage.url;
      image.srcset = hoverImage.url;
      image.alt = hoverImage.altText || product.title || "";

      hoverContainer.querySelectorAll("source").forEach((source) => {
        source.srcset = hoverImage.url;
      });

      hoverContainer.style.removeProperty("display");
    } else if (!hoverImage) {
      hoverContainer.style.display = "none";
    }
  }

  const priceSnippet = card.querySelector("price-snippet");
  const priceInfo = product.priceRange?.minVariantPrice;

  if (priceSnippet && priceInfo?.amount) {
    const amount = Number(priceInfo.amount);
    const isSwatchProduct = isSwatchProductFromTags(product.tags);
    const { price, compareAtPrice } = resolveDisplayPrice(amount, isSwatchProduct);

    priceSnippet.setAttribute("price", String(price));
    priceSnippet.setAttribute("compare-at-price", String(compareAtPrice));
    priceSnippet.setAttribute("currency", priceInfo.currencyCode || "USD");
    priceSnippet.dataset.basePrice = String(price);
    priceSnippet.dataset.baseCompareAtPrice = String(compareAtPrice);
    priceSnippet.dataset.rawPrice = String(amount);
    priceSnippet.dataset.swatchProduct = String(isSwatchProduct);
    priceSnippet.dataset.freePrice = String(isSwatchProduct && !window.customerHasFreeSwatch);
  }

  card.querySelectorAll("[data-product-card-swatch]").forEach((swatch) => {
    const active = swatch.dataset.productId === product.id;

    swatch.classList.toggle("is-active", active);
    swatch.setAttribute("aria-pressed", active ? "true" : "false");
  });

  const wishlistBtn = card.querySelector("[data-add-to-wishlist]");

  if (wishlistBtn) {
    wishlistBtn.dataset.wishlistProductId = numericIdFromGid(product.id) || "";
    wishlistBtn.dataset.wishlistVariantId = numericIdFromGid(product.variants?.nodes?.[0]?.id) || "";
    wishlistBtn.dataset.wishlistProductUrl = product.handle ? `${window.location.origin}${getProductUrl(product)}` : "";
  }

  applyAddToBagTarget(card, product);
}

function renderCardSwatches(card, products) {
  const container = card.querySelector(swatchContainerSelector);

  if (!container) {
    return;
  }

  const currentProductId = card.dataset.productId;
  const validProducts = products.filter((product) => product?.id && product?.handle);
  const sortedProducts = sortProductsForCard(validProducts, currentProductId);
  const visibleProducts = sortedProducts.slice(0, maxVisibleSwatches);
  const remainingCount = Math.max(sortedProducts.length - maxVisibleSwatches, 0);

  container.innerHTML = "";

  visibleProducts.forEach((product) => {
    const swatch = createSwatch(product, currentProductId, (selectedProduct) => {
      updateProductCard(card, selectedProduct);
    });

    container.appendChild(swatch);
  });

  if (remainingCount > 0) {
    container.appendChild(createMoreIndicator(remainingCount));
  }

  if (!sortedProducts.length) {
    container.style.display = "none";
  } else {
    container.style.removeProperty("display");
  }
}

export async function initializeProductCardSwatches(root = document) {
  const cards = [
    ...(root instanceof Element && root.matches(cardSelector) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(cardSelector) || []),
  ].filter(
    (card) =>
      card.dataset.productSwatchesInitialized !== "true" &&
      card.dataset.productSwatchesInitialized !== "loading" &&
      !card.closest("main-collection"),
  );

  if (!cards.length) {
    return;
  }

  cards.forEach((card) => {
    card.dataset.productSwatchesInitialized = "loading";
  });

  try {
    const groupIds = unique(cards.map((card) => card.dataset.productGroupId));

    if (!groupIds.length) {
      return;
    }

    const groups = await fetchProductGroups(groupIds);
    const bucketsByGroup = new Map();

    groups.forEach((group) => {
      const buckets = new Map();

      collectGroupBuckets(group, buckets);

      bucketsByGroup.set(group.id, buckets);
    });

    const allProductIds = unique(
      [...bucketsByGroup.values()].flatMap((buckets) => [...buckets.values()].flatMap((bucket) => bucket.productIds)),
    );

    const products = await fetchProducts(allProductIds);
    const productById = new Map(products.map((product) => [product.id, product]));

    cards.forEach((card) => {
      const groupId = card.dataset.productGroupId;
      const currentProductId = card.dataset.productId;
      const buckets = bucketsByGroup.get(groupId);

      if (!buckets) {
        card.dataset.productSwatchesInitialized = "true";
        return;
      }

      const colorFamily = findColorFamilyBucket(buckets, currentProductId);

      if (!colorFamily) {
        console.warn("[Product Card Swatches] Product not found in matching color family:", {
          productId: currentProductId,
          groupId,
        });

        card.dataset.productSwatchesInitialized = "true";
        return;
      }

      const siblingProducts = colorFamily.productIds.map((id) => productById.get(id)).filter(Boolean);

      renderCardSwatches(card, siblingProducts);

      card.dataset.productSwatchesInitialized = "true";
    });
  } catch (error) {
    console.error("[Product Card Swatches] Initialization failed:", error);

    cards.forEach((card) => {
      delete card.dataset.productSwatchesInitialized;
    });
  }
}

initializeProductCardSwatches();

document.addEventListener("shopify:section:load", (event) => {
  initializeProductCardSwatches(event.target);
});

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }

      const hasProductCards = node.matches?.(cardSelector) || node.querySelector?.(cardSelector);

      if (hasProductCards) {
        initializeProductCardSwatches(node);
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });
